import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { ImagePlus, Loader2, X, Info } from "lucide-react";

interface ReferenceDropzoneProps {
  previewUrl: string | null;
  uploading: boolean;
  onDrop: (file: File) => void;
  onClear: () => void;
  label?: string;
}

const ReferenceDropzone: React.FC<ReferenceDropzoneProps> = ({
  previewUrl,
  uploading,
  onDrop,
  onClear,
  label = "Reference image",
}) => {
  const handleDrop = useCallback(
    (accepted: File[]) => {
      if (accepted[0]) onDrop(accepted[0]);
    },
    [onDrop]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: { "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"], "image/webp": [".webp"] },
    maxFiles: 1,
    disabled: uploading,
  });

  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-muted">{label}</label>

      {previewUrl ? (
        <div className="relative group rounded-md overflow-hidden border border-border-default bg-surface-2">
          <img src={previewUrl} alt="reference" className="w-full max-h-40 object-contain" />
          <button
            type="button"
            onClick={onClear}
            title="Remove reference"
            className="absolute top-1.5 right-1.5 rounded-md bg-surface-overlay/90 p-1 text-muted hover:text-primary shadow"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`flex flex-col items-center justify-center gap-1.5 rounded-md border border-dashed px-3 py-6 text-center cursor-pointer transition-colors ${
            isDragActive
              ? "border-violet-500 bg-surface-brand"
              : "border-border-strong bg-surface-2 hover:bg-surface-3"
          }`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <Loader2 size={20} className="animate-spin text-violet-500" />
          ) : (
            <ImagePlus size={20} className="text-subtle" />
          )}
          <span className="text-xs text-muted">
            {uploading ? "Uploading…" : "Drop an image or click to upload"}
          </span>
          <span className="text-[11px] text-subtle">PNG, JPG or WebP</span>
        </div>
      )}

      {!previewUrl && (
        <div className="flex items-start gap-1.5 rounded-md bg-surface-2 px-2.5 py-2 text-[11px] leading-snug text-subtle">
          <Info size={13} className="mt-0.5 shrink-0 text-muted" />
          <span>
            <span className="text-muted">Any size works — no need to resize first.</span> Best
            quality up to ~9 MP with a ratio between 1:2.5 and 2.5:1. Larger photos are
            optimized automatically: e.g. a{" "}
            <span className="text-muted">24 MP / 30 MB</span> photo is resized to{" "}
            <span className="text-muted">~2048 px (≈4 MP)</span> — fast upload, still sharp.
            Very wide or tall panoramas (beyond 2.5:1) should be cropped first.
          </span>
        </div>
      )}
    </div>
  );
};

export default ReferenceDropzone;
